'use client';

import { c as _c } from "react/compiler-runtime";
import type { ButtonState } from '@morfi/how-morfi-works';
import { useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { SimulatedTradeStage, type StageControl } from '@/components/simulated-trade/simulated-trade-stage';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InfoModal } from '@/components/ui/info-modal';
import { useSimulatedTradeModalStore } from '@/stores/simulated-trade-modal';

/**
 * "View interactive explainer" — the six-stage simulated market (spec 115).
 * Store-driven and mounted once at the AppShell root, like the graduation
 * explainer; the how-it-works modals hand off to it. The stage remounts on
 * every open so the simulation always starts from the intro.
 */
export function SimulatedTradeModal() {
  const $ = _c(19);
  const t = useTranslations("simulated_trade");
  const open = useSimulatedTradeModalStore(_temp);
  const setOpen = useSimulatedTradeModalStore(_temp2);
  const reduceMotion = useReducedMotion();
  const controlRef = useRef(null);
  let t0;
  if ($[0] !== t) {
    t0 = () => ({
      label: t("intro_button"),
      disabled: true,
      spinner: false
    });
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const introButton = t0;
  const [button, setButton] = useState(introButton);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setButton(introButton());
    }
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("title");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => controlRef.current?.next();
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== button.disabled || $[6] !== button.label || $[7] !== button.spinner) {
    t3 = <AnimatedButton size="xl" className="w-full" disabled={button.disabled} loading={button.spinner} onClick={t2}>{button.label}</AnimatedButton>;
    $[5] = button.disabled;
    $[6] = button.label;
    $[7] = button.spinner;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== open || $[10] !== reduceMotion || $[11] !== setOpen) {
    t4 = open ? <SimulatedTradeStage isStatic={reduceMotion === true} controlRef={controlRef} onButton={setButton} onClose={() => setOpen(false)} /> : null;
    $[9] = open;
    $[10] = reduceMotion;
    $[11] = setOpen;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== open || $[14] !== setOpen || $[15] !== t1 || $[16] !== t3 || $[17] !== t4) {
    t5 = <InfoModal open={open} onOpenChange={setOpen} size="lg" title={t1} bodyClassName="gap-0" footer={t3}>{t4}</InfoModal>;
    $[13] = open;
    $[14] = setOpen;
    $[15] = t1;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
}
function _temp2(s_0) {
  return s_0.setOpen;
}
function _temp(s) {
  return s.open;
}